import {Injectable} from '@angular/core';

/**
 * Theme name the CXone container reports for Lyra.
 */
export const LYRA_THEME = 'lyra';

/**
 * Theme payload as sent by the CXone MFE wrapper, both on the `auth` message
 * (under `theme`) and as the whole payload of a `theme` message.
 */
export interface ThemeInfo {
  theme: string | null;
  isLyra: boolean;
}

/**
 * Records the theme the CXone container reports over the MFE bridge.
 *
 * ESP always renders in Lyra (`data-theme="lyra"` is set on <body> in
 * index.html), in CXone and standalone (IEX/WCX) alike, so the reported theme
 * no longer changes ESP's look. It is kept so the bridge contract stays intact
 * and the container's theme is available if ESP needs it again (e.g. for a
 * future dark theme).
 */
@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private _theme: string | null = null;
  private _isLyra = false;

  /** Theme name last reported by the container, or null when none was declared. */
  get theme(): string | null {
    return this._theme;
  }

  /** True when the container last reported a Lyra-family theme. */
  get isLyra(): boolean {
    return this._isLyra;
  }

  /**
   * Records the reported theme.
   *
   * Tolerates a missing or malformed payload: anything we cannot read as a
   * theme is recorded as "no theme" rather than throwing, so an older MFE that
   * sends no theme at all keeps working.
   */
  applyTheme(info: ThemeInfo | null | undefined): void {
    this._theme = null;
    this._isLyra = false;

    if (info && typeof info === 'object') {
      this._theme = typeof info.theme === 'string' ? info.theme : null;
      // The contract says to branch on isLyra rather than string-matching the
      // theme name. Fall back to the name only when isLyra is absent.
      this._isLyra = typeof info.isLyra === 'boolean' ? info.isLyra : this._theme === LYRA_THEME;
    }
  }
}
